import moment from "moment";
import { Revision, RawApiRevision } from "shared/types/revision";
import { getDisplayFileName } from "shared/utils/helper";
import { getUploadKind } from "shared/constants/officeFiles";

const STAMP_DATE_FORMAT = "MM/DD/YYYY HH:mm";

const mapStatus = (
  apiStatus?: string,
): "In Review" | "Approved" | "Rejected" | undefined => {
  if (!apiStatus) return undefined;
  const statusLower = apiStatus.toLowerCase();
  if (statusLower.includes("approved") || statusLower.includes("complete")) {
    return "Approved";
  }
  if (statusLower.includes("rejected")) {
    return "Rejected";
  }
  if (
    statusLower.includes("waiting") ||
    statusLower.includes("review") ||
    statusLower.includes("pending")
  ) {
    return "In Review";
  }
  return "In Review";
};

const getFileNameFromPath = (path: string): string => getDisplayFileName(path);

export const transformRevisions = (
  rawRevisions: RawApiRevision[],
): Revision[] => {
  return rawRevisions.map((rev) => {
    const designerUploads = rev.designer_uploads || rev.files || [];
    const clientUploads = rev.inspirations || [];

    return {
      id: rev.revision_id?.toString() || rev.id?.toString() || "",
      title: rev.title || `Revision ${rev.revision_id || ""}`,
      badgeTitle: rev.status || "",
      date: moment(
        rev.designer_submission_date || rev.created_at || rev.date || new Date(),
      ).format(STAMP_DATE_FORMAT),
      author:
        rev.designer?.display_name ||
        (rev.designer?.first_name && rev.designer?.last_name
          ? `${rev.designer.first_name} ${rev.designer.last_name}`
          : "") ||
        rev.author ||
        rev.designer_name ||
        rev.created_by ||
        "",
      fileCount: designerUploads.length || 0,
      // Client stamp stays empty until the client actually submits feedback.
      clientFeedbackDate: rev.client_feedback_date
        ? moment(rev.client_feedback_date).format(STAMP_DATE_FORMAT)
        : "",
      clientFeedbackAuthor:
        rev.feedback_user?.display_name || rev.feedback_user?.email || "",
      clientFileCount: clientUploads.length || 0,
      clientFiles: clientUploads.map((file) => {
        const name = file.name || getFileNameFromPath(file.path || "");
        return {
          id:
            file.revision_inspiration_id?.toString() ||
            file.id?.toString() ||
            "",
          name,
          type: getUploadKind({ type: file.type, name }) as
            | "pdf"
            | "image"
            | "document",
          thumbnail: file.path || "",
        };
      }),
      status: mapStatus(rev.status),
      isFinalPlans: !!rev.is_final_plans,
      mediaSort: rev.media_sort ?? null,
      files: designerUploads.map((file) => ({
        id:
          file.revision_designer_upload_id?.toString() ||
          file.id?.toString() ||
          file.file_id?.toString() ||
          "",
        name:
          file.name || file.file_name || getFileNameFromPath(file.path || ""),
        type: getUploadKind({
          type: file.type,
          name:
            file.name || file.file_name || getFileNameFromPath(file.path || ""),
        }) as "pdf" | "image" | "document",
        thumbnail: file.path || file.thumbnail || file.thumbnail_path || "",
      })),
      commentsFromDesigner:
        rev.designer_comment ||
        rev.comments_from_designer ||
        rev.designer_comments ||
        "",
      commentsToClient:
        rev.client_comment || rev.comments_to_client || rev.client_comments || "",
      internalComments: rev.internal_comment || rev.internal_comments || "",
      clientChangesRequest:
        rev.client_changes_request || rev.changes_request || "",
    };
  });
};
